const BLOCK_START_PATTERN = /\{\{observation-start:([a-zA-Z0-9_-]+)\}\}/g;
const BLOCK_MARKER_PATTERN = /\{\{observation-(?:start|end):[a-zA-Z0-9_-]+\}\}/g;
const EMBED_PATTERN = /\{\{observation:([^:{}]+):([^:{}]+)\}\}/g;

const decodeTokenPart = (value) => {
  try {
    return decodeURIComponent(value);
  } catch (error) {
    return value;
  }
};

export function createObservationId(now = Date.now(), randomValue = Math.random()) {
  return `obs_${Number(now).toString(36)}_${Math.floor(randomValue * 0xFFFFFF).toString(36)}`;
}

export function createObservationEmbed(sourcePageId, blockId) {
  return `{{observation:${encodeURIComponent(String(sourcePageId))}:${encodeURIComponent(String(blockId))}}}`;
}

export function appendObservationEmbed(content, embed) {
  const input = String(content || '');
  if (!embed || input.includes(embed)) return input;
  if (!input.trim()) return embed;
  return `${input.replace(/\s+$/, '')}\n\n${embed}`;
}

export function removeObservationEmbed(content, embed) {
  if (!embed) return String(content || '');
  return String(content || '')
    .replace(embed, '')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/^\n+|\n+$/g, '');
}

export function stripObservationBlockMarkers(content) {
  return String(content || '').replace(BLOCK_MARKER_PATTERN, '');
}

export function stripObservationEmbeds(content) {
  return String(content || '').replace(new RegExp(EMBED_PATTERN.source, 'g'), '');
}

export function parseObservationEmbeds(content) {
  const input = String(content || '');
  const embeds = [];
  const regex = new RegExp(EMBED_PATTERN.source, 'g');
  let match;
  while ((match = regex.exec(input)) !== null) {
    embeds.push({
      match: match[0],
      sourcePageId: decodeTokenPart(match[1]),
      blockId: decodeTokenPart(match[2]),
      startIndex: match.index,
      endIndex: match.index + match[0].length,
    });
  }
  return embeds;
}

export function parseObservationBlocks(content) {
  const input = String(content || '');
  const blocks = [];
  const startRegex = new RegExp(BLOCK_START_PATTERN.source, 'g');
  let match;
  while ((match = startRegex.exec(input)) !== null) {
    const blockId = match[1];
    const endMarker = `{{observation-end:${blockId}}}`;
    const textStart = match.index + match[0].length;
    const endIndex = input.indexOf(endMarker, textStart);
    if (endIndex < 0) continue;
    blocks.push({
      blockId,
      markerStart: match.index,
      textStart,
      textEnd: endIndex,
      markerEnd: endIndex + endMarker.length,
      text: input.slice(textStart, endIndex),
    });
  }
  return blocks;
}

export function addObservationBlock(content, selectionStart, selectionEnd, blockId) {
  const input = String(content || '');
  const start = Math.max(0, Math.min(Number(selectionStart) || 0, input.length));
  const end = Math.max(start, Math.min(Number(selectionEnd) || 0, input.length));
  if (end <= start || !input.slice(start, end).trim()) {
    return { content: input, blockId: null, changed: false, reason: 'empty_selection' };
  }

  const blocks = parseObservationBlocks(input);
  const containingBlock = blocks.find((block) => start >= block.textStart && end <= block.textEnd);
  if (containingBlock) {
    return { content: input, blockId: containingBlock.blockId, changed: false, reason: 'existing_block' };
  }

  const overlapsBlock = blocks.some((block) => start < block.markerEnd && end > block.markerStart);
  if (overlapsBlock) {
    return { content: input, blockId: null, changed: false, reason: 'overlapping_block' };
  }

  const nextBlockId = blockId || createObservationId();
  const startMarker = `{{observation-start:${nextBlockId}}}`;
  const endMarker = `{{observation-end:${nextBlockId}}}`;
  return {
    content: `${input.slice(0, start)}${startMarker}${input.slice(start, end)}${endMarker}${input.slice(end)}`,
    blockId: nextBlockId,
    changed: true,
    reason: 'created_block',
  };
}

export function findObservationSource(sections, sourcePageId, blockId) {
  for (const section of sections || []) {
    if (section?.isFolder || !Array.isArray(section?.pages)) continue;
    const page = section.pages.find((candidate) => String(candidate.id) === String(sourcePageId));
    if (!page) continue;
    const block = parseObservationBlocks(page.content).find((candidate) => candidate.blockId === blockId);
    return {
      found: Boolean(block),
      sectionId: section.id,
      sectionName: section.name || 'Untitled Section',
      pageId: page.id,
      pageName: page.name || 'Untitled Page',
      text: block?.text || '',
    };
  }
  return { found: false, pageId: sourcePageId, blockId, text: '' };
}
